import { Box, Menu, MenuItem, Typography } from '@mui/material';
import {
  type FC,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useMemo,
  useState,
} from 'react';

export interface SlabMenuComponentProps {
  onClick(e: MouseEvent<HTMLButtonElement>): void;
}

export enum SlabMenuAlign {
  Left = 'left',
  Right = 'right',
}

type OptionProps = {
  hidden?: boolean;
  icon?: ReactElement;
  label: string;
  description?: ReactNode;
  onClick(): void;
};

type SlabMenuProps = {
  component: FC<SlabMenuComponentProps>;
  options: OptionProps[];
  align?: SlabMenuAlign;
};

export const SlabMenu: FC<SlabMenuProps> = ({
  component: Component,
  options,
  align = SlabMenuAlign.Left,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const filteredOptions = useMemo(
    () => options.filter((option) => option.hidden !== true),
    [options],
  );

  const isOpen = anchorEl !== null;

  const openMenu = (e: MouseEvent<HTMLButtonElement>): void => setAnchorEl(e.currentTarget);

  const closeMenu = (): void => setAnchorEl(null);

  const handleOptionPress = (onClick: () => void) => (): void => {
    onClick();
    closeMenu();
  };

  return (
    <>
      <Component onClick={openMenu} />
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: align }}
        transformOrigin={{ vertical: 'top', horizontal: align }}
        sx={{ margin: 1 }}
      >
        {filteredOptions.map(({ label, description, icon, onClick }) => (
          <MenuItem
            sx={{ padding: 1.5, display: 'flex', gap: 1 }}
            key={label}
            onClick={handleOptionPress(onClick)}
          >
            {icon}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={{ fontWeight: 500 }} variant='body2'>
                {label}
              </Typography>
              <Typography variant='body3'>{description}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
